import axios from 'axios';
import type { PublicCampaign, CaptureLeadRequest, LeadDto } from '@/types/public';

/**
 * Public API service for the campaign widget.
 *
 * Uses a separate axios instance without auth interceptors
 * since these endpoints are publicly accessible.
 */
const publicApi = axios.create({
  baseURL: import.meta.env.VITE_API_URL ?? '/api',
  headers: {
    'Content-Type': 'application/json',
    ...(import.meta.env.VITE_TENANT_ID
      ? { 'X-Tenant-Id': import.meta.env.VITE_TENANT_ID }
      : {}),
  },
  timeout: 15_000,
});

const BASE_PATH = '/public';

export const publicService = {
  /**
   * Fetches all active campaigns with their packages.
   * Used by the home page to display available campaigns.
   */
  async getActiveCampaigns(): Promise<PublicCampaign[]> {
    const { data } = await publicApi.get<PublicCampaign[]>(
      `${BASE_PATH}/campaigns`,
    );
    return data;
  },

  /**
   * Fetches a campaign by its public slug.
   * Includes packages with procedure names.
   */
  async getCampaignBySlug(slug: string): Promise<PublicCampaign> {
    const { data } = await publicApi.get<PublicCampaign>(
      `${BASE_PATH}/campaigns/${slug}`,
    );
    return data;
  },

  /**
   * Captures a lead from the campaign widget form.
   * Includes UTM tracking parameters.
   */
  async captureLead(payload: CaptureLeadRequest): Promise<LeadDto> {
    const { data } = await publicApi.post<LeadDto>(
      `${BASE_PATH}/leads`,
      payload,
    );
    return data;
  },
};
